<script setup lang="ts">
/**
 * A row of headline figures.
 *
 * Laid out on a grid that fits what is there rather than a fixed count, so
 * three figures fill the width and six wrap into two rows of three instead of
 * shrinking until they cannot be read.
 *
 * Movement is coloured only when the author said which way is good. A figure
 * that fell is not automatically bad, and colouring it red because it went
 * down is the interface asserting something about the business it cannot know.
 */
import type { Kpi, KpiSection } from '@/domain/section'

defineProps<{ section: KpiSection }>()

function toneOf(item: Kpi): string {
  if (item.direction === 'up') return 'kpi--good'
  if (item.direction === 'down') return 'kpi--poor'
  return ''
}
</script>

<template>
  <div class="kpis">
    <div v-for="item in section.items" :key="item.id" class="kpi">
      <p class="kpi__label">{{ item.label }}</p>
      <p class="kpi__value">{{ item.value }}</p>
      <p v-if="item.change" class="kpi__change" :class="toneOf(item)">{{ item.change }}</p>
    </div>
  </div>
</template>

<style scoped>
.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(38mm, 1fr));
  gap: 5mm;
  margin: 1em 0 1.2em;
}

.kpi {
  /* A rule at the top rather than a box: it groups the figure with its
     label without adding a container the page does not need. */
  padding-top: 2.5mm;
  border-top: 1.2px solid var(--page-accent);
}

.kpi__label {
  margin: 0 0 1.5mm;
  color: var(--paper-ink-soft);
  font-size: 8.5pt;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: left;
}

.kpi__value {
  margin: 0;
  font-size: 17pt;
  font-weight: 600;
  line-height: 1.15;
  font-variant-numeric: tabular-nums lining-nums;
  text-align: left;
}

.kpi__change {
  margin: 1mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 9pt;
  font-variant-numeric: tabular-nums lining-nums;
  text-align: left;
}

.kpi--good {
  color: #0f7a4a;
}

.kpi--poor {
  color: #a3343c;
}
</style>
